import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Stack,
  Typography,
  IconButton,
  TextField,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Edit,
  DeleteOutline,
  Check,
  Close,
  DescriptionOutlined,
} from '@mui/icons-material';
import { formatLetterDate, letterPreview } from '../../utils/utils';

export default function LetterCard({
  letter,
  onOpen,
  onRename,
  onDelete,
  renaming,
}) {
  const [editing, setEditing] = useState(false);
  const [nameValue, setNameValue] = useState(letter.name || '');

  useEffect(() => {
    setNameValue(letter.name || '');
  }, [letter.name]);

  const commitRename = async () => {
    const trimmed = nameValue.trim();
    if (trimmed && trimmed !== letter.name) {
      await onRename(letter.id, trimmed);
    }
    setEditing(false);
  };

  const cancelRename = () => {
    setNameValue(letter.name || '');
    setEditing(false);
  };

  return (
    <Card
      sx={{
        width: { xs: '100%', sm: 320 },
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        boxShadow: 3,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
        },
      }}
    >
      <CardContent
        onClick={() => !editing && onOpen(letter)}
        sx={{
          cursor: editing ? 'default' : 'pointer',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          spacing={1}
          sx={{ mb: 0.5 }}
        >
          <DescriptionOutlined color="primary" sx={{ mt: '2px' }} />
          {editing ? (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ flexGrow: 1 }}
              onClick={(e) => e.stopPropagation()}
            >
              <TextField
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitRename();
                  if (e.key === 'Escape') cancelRename();
                }}
                autoFocus
                fullWidth
                size="small"
                variant="standard"
                inputProps={{ maxLength: 120 }}
              />
              <IconButton
                onClick={commitRename}
                disabled={renaming}
                color="primary"
                size="small"
              >
                {renaming ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Check fontSize="small" />
                )}
              </IconButton>
              <IconButton onClick={cancelRename} size="small">
                <Close fontSize="small" />
              </IconButton>
            </Stack>
          ) : (
            <Typography
              variant="h6"
              component="h3"
              sx={{
                fontWeight: 'bold',
                lineHeight: 1.25,
                flexGrow: 1,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {letter.name}
            </Typography>
          )}
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
          {formatLetterDate(letter.createdAt)}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {letterPreview(letter.generatedLetter, 220)}
        </Typography>
      </CardContent>

      <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 1.5 }}>
        <Tooltip title="Rename">
          <span>
            <IconButton
              onClick={() => setEditing(true)}
              disabled={editing}
              size="small"
            >
              <Edit fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            onClick={() => onDelete(letter)}
            size="small"
            sx={{ '&:hover': { color: 'error.main' } }}
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
}
